<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { vMaska } from "maska/vue";
import {
  useValidatorField,
  type BaseValidatorProps,
} from "@/composables/ui/inputs/useValidatorField";
import { useI18n } from "vue-i18n";
import { formatForElementId, toCamelCase } from "@/utils/Formatters";
import type { ESubjects } from "@/api/models/auth/ability";
import { useAbility } from "@casl/vue";
import { VTextField, VTextarea } from "vuetify/components";
import { cleanErrorMessage } from "@/utils/FormUtils.ts";
import CompactTextMenu from "@/components/ui/inputs/compact/CompactTextMenu.vue";
import { useCompactScope } from "@/composables/shared/useCompactScope";

interface Props extends BaseValidatorProps {
  modelValue?: string;
  maxLength?: number; // single source of truth for char limit
  type?: "text" | "textarea" | "password" | "email" | "tel" | "number";
  autocomplete?: string;
  mask?: string | string[];
  prefix?: string;
  subject?: ESubjects;
  // will work only with number type
  min?: number;
  max?: number;
  step?: number | "any";
  /** v-textarea: limits auto-grow height; extra content scrolls inside the field */
  maxRows?: number;
  hint?: string;
  /** Optional extra icon rendered before the validation icon in the append-inner slot */
  appendInnerIcon?: string;
  /** Optional icon rendered inside the field, before the typed text */
  prependInnerIcon?: string;
}

const emit = defineEmits<{
  (e: "click:appendInner"): void;
  (e: "click:prependInner"): void;
}>();

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
  variant: "outlined",
  density: "comfortable",
  hideDetails: false,
  disabled: false,
  readonly: false,
  type: "text",
  autocomplete: "off",
  required: false,
  transparent: false,
  showValidationIcon: true,
  touched: false,
  maxLength: undefined,
  withWarning: null,
  min: undefined,
  max: undefined,
  step: 1,
  hint: undefined,
});

const isFocused = ref(false);
const { can } = useAbility();
const { isCompact } = useCompactScope();

const isFieldHidden = computed(() => {
  return props.subject && props.fieldName && !can($permActions.READ, props.subject);
});

const permissionDisabled = computed(() => {
  return Boolean(
    props.disabled ||
    (props.subject &&
      props.fieldName &&
      !can($permActions.CREATE, props.subject) &&
      !can($permActions.UPDATE, props.subject))
  );
});

const {
  value,
  errorMessage,
  validationIcon,
  validationColor,
  baseFieldProps,
  warnings,
  setTouched,
} = useValidatorField(props, isFocused);

watch(
  () => value.value,
  (val, oldVal) => {
    if (props.maxLength && typeof val === "string") {
      const effectiveLength = props.mask ? val.replaceAll(/\s/g, "").length : val.length;
      if (effectiveLength > props.maxLength) {
        value.value = props.mask ? (oldVal ?? "") : val.slice(0, props.maxLength);
        return;
      }
    }
    if (props.type !== "number" || props.max === undefined) {
      return;
    }
    if (val === "" || val === null || val === undefined) {
      return;
    }
    const n = typeof val === "number" ? val : Number(val);
    if (Number.isNaN(n)) {
      return;
    }
    if (n > props.max) {
      value.value = oldVal ?? "";
    }
  }
);

const isWarningWhileFocused = computed(() => !!warnings.message.value && isFocused.value);
const displayColor = computed(() =>
  isWarningWhileFocused.value ? "warning" : validationColor.value
);

const compLabel = computed(
  () => props.label ?? t(`generic.fields.${toCamelCase(props.fieldName)}`)
);

const textFieldProps = computed(() => {
  const base: Record<string, unknown> = {
    ...baseFieldProps.value,
    maxLength: props.mask ? undefined : props.maxLength,
    label: compLabel.value,
    placeholder: props.readonly
      ? undefined
      : props.placeholder || t("generic.action.enterEntity", { entity: compLabel.value }),
    type: props.type,
    autocomplete: props.autocomplete,
    prefix: props.prefix,
    error: !!errorMessage.value && !warnings.focused.value,
    errorMessages:
      errorMessage.value && !warnings.focused.value
        ? cleanErrorMessage(errorMessage.value, t)
        : undefined,
    messages:
      warnings.message.value && (warnings.only.value || warnings.focused.value)
        ? cleanErrorMessage(warnings.message.value, t)
        : undefined,
    color: warnings.only.value || warnings.focused.value ? "warning" : validationColor.value,
    persistentPlaceholder: true,
    counter: props.maxLength ?? undefined,
    counterValue:
      props.mask && props.maxLength
        ? (val: string) => (val ?? "").replaceAll(/\s/g, "").length
        : undefined,
    class: `${baseFieldProps.value.class || ""}`.trim(),
    readonly: props.readonly,
    name: props.fieldName,
    hint: props.hint,
    persistentHint: !!props.hint,
    prependInnerIcon: props.prependInnerIcon,
  };

  if (props.type === "number") {
    base.min = props.min;
    base.max = props.max;
    base.step = props.step;
  }

  // v-text-field only
  if (props.type === "text") {
    base.type = "text"; // only v-text-field cares
    if (props.prefix != null) {
      base.prefix = props.prefix;
    }
  }

  // Add inputmode for number fields using text type
  if (props.type === "text" && props.fieldName?.includes("price")) {
    base.inputmode = "numeric";
  }

  /*
    Add inputmode for email fields using text type
    As input mode is interpreted by some OS, specify this to avoid unwanted behaviors
  */
  if (props.type === "email") {
    base.type = "text";
    base.inputmode = "email";
  }

  if (props.type === "tel") {
    base.inputmode = "tel";
  }

  // v-textarea only
  if (props.type === "textarea") {
    base.autoGrow = true;
    if (props.maxRows != null) {
      base.maxRows = props.maxRows;
    }
  }

  return base;
});

const elementId = computed(() => formatForElementId(textFieldProps.value.label as string));

const inputComponent = computed(() => (props.type === "textarea" ? VTextarea : VTextField));

const commonProps = computed(() => ({
  ...textFieldProps.value,
  modelValue: props.readonly && props.modelValue !== undefined ? props.modelValue : value.value,
  "onUpdate:modelValue": (val: string | number | null) => {
    if (props.readonly && props.modelValue !== undefined) {
      return;
    }
    value.value = val;
  },
  class: [textFieldProps.value.class, props.class, { "v-input--required": props.required }],
  disabled: permissionDisabled.value,
  "aria-autocomplete": "none",
  "onClick:prependInner": () => emit("click:prependInner"),
  "onUpdate:focused": (val: boolean) => {
    isFocused.value = val;
    if (!val) {
      setTouched(true);
    }
  },
  "data-testid": "text-field-validator",
  "data-test-elementid": elementId.value,
}));

const isCompactTextarea = computed(() => isCompact.value && props.type === "textarea");

const isCompactEditable = computed(() => !permissionDisabled.value && !props.readonly);

const compactValue = computed(() => {
  const current = commonProps.value.modelValue;
  return typeof current === "string" ? current : undefined;
});
</script>

<template>
  <compact-text-menu
    v-if="!isFieldHidden && isCompactTextarea"
    :caption="compLabel"
    :value="compactValue"
    :error="!!errorMessage"
    :editable="isCompactEditable"
  >
    <component
      :is="inputComponent"
      v-bind="commonProps"
      :rows="4"
    />
  </compact-text-menu>

  <component
    :is="inputComponent"
    v-else-if="!isFieldHidden && props.mask"
    v-maska="{ mask: props.mask, tokens: { A: { pattern: /[A-Z]/ }, N: { pattern: /[A-Z0-9]/ } } }"
    v-bind="commonProps"
  >
    <template
      v-if="(props.showValidationIcon && !props.readonly) || props.appendInnerIcon"
      #append-inner
    >
      <v-icon
        v-if="props.appendInnerIcon"
        :icon="props.appendInnerIcon"
        class="tw:cursor-pointer tw:mr-2!"
        @click="emit('click:appendInner')"
      />
      <v-icon
        v-if="props.showValidationIcon && !props.readonly"
        :icon="validationIcon"
        :color="displayColor"
        class="tw:transition-all tw:duration-300 tw:ease-out tw:opacity-100"
      />
    </template>
  </component>
  <component
    :is="inputComponent"
    v-else-if="!isFieldHidden"
    v-bind="commonProps"
  >
    <template
      v-if="(props.showValidationIcon && !props.readonly) || props.appendInnerIcon"
      #append-inner
    >
      <v-icon
        v-if="props.appendInnerIcon"
        :icon="props.appendInnerIcon"
        class="tw:cursor-pointer tw:mr-2!"
        @click="emit('click:appendInner')"
      />
      <v-icon
        v-if="props.showValidationIcon && !props.readonly"
        :icon="validationIcon"
        :color="displayColor"
        class="tw:transition-all tw:duration-300 tw:ease-out tw:opacity-100"
      />
    </template>
  </component>
</template>

<style src="@/assets/styles/components/fields.scss" scoped lang="scss" />

<style scoped lang="scss">
.v-field__append-inner > .v-icon {
  opacity: 1 !important;
}
</style>
